import { useTranslation } from 'gatsby-plugin-react-i18next'
import { get } from 'lodash'
import React from 'react'

import useHomepage from '../../hooks/useHomepage'
import Plan3MealRolloverImage from '../../images/plan-3-meal-rollover.jpg'
import Plan12MealImage from '../../images/plan-12-meal-rollover.png'
import Plan16MealImage from '../../images/plan-16-meal-rollover.png'
import UtmContent from '../../models/UtmContent'
import { AnalyticsService, UrlService } from '../../services'
import { Plan as PlanType } from '../../types'
import ScrollAnimation from '../scroll-animation/Container'

type Props = {
  plan: PlanType
  animation: string
  index: number
}

const Plan: React.FC<Props> = ({ plan, animation, index }) => {
  const { t } = useTranslation()
  const {
    productOffering,
    showModal,
    launchConfirmationRequired,
    animationProps,
    onNyLunchPage,
    onSfLunchPage,
    onNyDinnerPage,
    landingPageAlias,
  } = useHomepage()

  const onClickHandler = (e: React.MouseEvent, plan: PlanType) => {
    if (launchConfirmationRequired) {
      e.preventDefault()

      return showModal({ name: 'get-access-form' })
    }

    AnalyticsService.trackEvent({
      action: 'Click',
      category: 'Homepage',
      label: 'Choose this plan',
      value: plan.id,
    })
  }

  const buildPlanHref = (plan: PlanType): string => {
    const utmContent = new UtmContent({
      plan,
      onNyLunchPage,
      onSfLunchPage,
      onNyDinnerPage,
      landingPageAlias,
    }).toString()

    return UrlService.build(utmContent, plan)
  }

  const mealCreditsIssued = Number(get(plan, 'plan_meal_kits.standard.meal_credits_issued', null))
  const leftPlan =
    plan?.trial ||
    mealCreditsIssued === 12 ||
    (mealCreditsIssued <= 90 && mealCreditsIssued >= 30 && plan?.city?.name.toLowerCase() != 'san francisco')

  // for 60 and 90 credit plans
  let rolloverCopyDinner = t('Roll over up to 6 meals')

  if (landingPageAlias == 'nydinner') {
    rolloverCopyDinner = t('')
  } else if (mealCreditsIssued == 105) {
    rolloverCopyDinner = t('Roll over up to 10 meals')
  } else if (mealCreditsIssued == 180) {
    rolloverCopyDinner = t('Roll over up to 12 meals')
  }

  // for 12 meal plans (suburbs) or 30 - 75 credit plans
  let rolloverCopyLunch = t('Roll over up to 12 meals')

  if (mealCreditsIssued == 16) {
    // for suburb 16-meal plans
    rolloverCopyLunch = t('Roll over up to 16 meals')
  } else if (mealCreditsIssued >= 105 && mealCreditsIssued <= 125) {
    rolloverCopyLunch = t('Roll over up to 20 meals')
  } else if (plan?.trial) {
    rolloverCopyLunch = t('Renews to 70 credit lunch')
  }

  const overrideProductOffering = productOffering === 'delivery' ? 'dinner' : productOffering
  const dinnerTrial = plan?.trial && productOffering === 'dinner'

  let plansAsLowAs = t('as low as')
  let planPerMealCopy = plan.planBoxPrice

  if (dinnerTrial) {
    plansAsLowAs = t('try it for')
    planPerMealCopy = t('FREE')
  }

  return (
    <ScrollAnimation key={index} {...animationProps} delay={onNyLunchPage || onSfLunchPage ? 0 : 200 * index + 600}>
      <div className="plans__box">
        <div className={`plans__box-title plans__box-title--${productOffering}`}>{plan.planBoxTitle}</div>
        {plan.spending_strategy === 'credits' && (
          <div className={`plans__credits plans__credits--${productOffering}`}>
            <div className="plans__credits-title">{plan.plan_meal_kits.standard.meal_credits_issued}</div>
            <div className="plans__credits-subtitle">credits</div>
            <div className="plans__credits-price">for {plan.display_price}</div>
          </div>
        )}
        {leftPlan ? (
          <a href={buildPlanHref(plan)} onClick={(e) => onClickHandler(e, plan)}>
            <img className="plans__image" src={plan.trial ? Plan3MealRolloverImage : Plan12MealImage} />
          </a>
        ) : (
          <a href={buildPlanHref(plan)} onClick={(e) => onClickHandler(e, plan)}>
            <img
              className="plans__image"
              src={
                plan?.city?.name.toLowerCase() == 'san francisco' && plan?.city?.name.toLowerCase() == 'seattle'
                  ? Plan12MealImage
                  : Plan16MealImage
              }
            />
          </a>
        )}
        <div className="plans__box-info">
          <div className="plans__renew">
            {plan.trial ? <span>{t('1 Week Trial')}</span> : <span>{t('30 Day Cycle')}</span>}
            {overrideProductOffering === 'dinner' ? (
              <span>{rolloverCopyDinner}</span>
            ) : (
              <>
                <span className="plans__separator">|</span>
                <span>{rolloverCopyLunch}</span>
              </>
            )}
          </div>
          <div className="plans__price">
            <div>
              <div className="plans__as-low">{plansAsLowAs}</div>
              {dinnerTrial ? <span className="plans__free">{planPerMealCopy}</span> : <span>{planPerMealCopy}</span>}
              {animation && plan?.trial && (
                <div className="plans__animation-image">
                  <img src={animation} />
                </div>
              )}
            </div>
            <a href={buildPlanHref(plan)} onClick={(e) => onClickHandler(e, plan)} className="plans__action red-cta">
              {t('Choose this plan')}
            </a>
          </div>
        </div>
      </div>
    </ScrollAnimation>
  )
}

export default Plan
